import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PauseCircle, Search } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useListState } from "../../hooks/useListState";
import { formatDate } from "../../lib/utils";

const TIME_FILTERS = [
  { value: "both", label: "Past & Future" }, { value: "future", label: "Upcoming" }, { value: "past", label: "Past" },
];

// Paused days never create a Delivery row (they're skipped entirely), so this
// pulls from a dedicated endpoint rather than the deliveries list/filters above.
export function PausedTiffinsPanel() {
  const [showAll, setShowAll] = useListState("deliveries.paused.showAll", false);
  const [timeFilter, setTimeFilter] = useListState("deliveries.paused.timeFilter", "both");
  const [q, setQ] = useListState("deliveries.paused.q", "");
  const debouncedQ = useDebouncedValue(q, 400);
  const [state, setState] = useState({ loading: true, error: null, total: 0, items: [] });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await apiClient.get("/deliveries/paused-tiffins", {
        params: {
          scope: showAll ? "all" : "today",
          time_filter: showAll ? timeFilter : undefined,
          q: debouncedQ || undefined,
          limit: 100,
        },
      });
      setState({ loading: false, error: null, total: data.total, items: data.items });
    } catch (error) {
      setState({ loading: false, error: getApiErrorMessage(error, "Could not load paused tiffins."), total: 0, items: [] });
    }
  }, [showAll, timeFilter, debouncedQ]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="mb-4 rounded-md border border-border bg-surface p-4" data-testid="paused-tiffins-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-text-main">
          <PauseCircle size={16} strokeWidth={1.75} className="text-warning" />
          {showAll ? "All Paused Tiffins" : "Paused Today"}
          <span className="rounded-full bg-warning-bg px-2 py-0.5 text-xs font-medium text-warning" data-testid="paused-tiffins-count">
            {state.total}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {showAll && (
            <Select value={timeFilter} onValueChange={setTimeFilter}>
              <SelectTrigger className="h-8 w-36 text-xs" data-testid="paused-tiffins-time-filter-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIME_FILTERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <div className="relative w-48">
            <Search size={13} strokeWidth={1.5} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
            <Input
              className="h-8 pl-8 text-xs"
              placeholder="Search customer..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="paused-tiffins-search-input"
            />
          </div>
          <Button
            type="button" size="sm" variant={showAll ? "default" : "outline"}
            onClick={() => setShowAll(!showAll)}
            data-testid="paused-tiffins-show-all-toggle"
          >
            {showAll ? "Show Today Only" : "Show All Paused"}
          </Button>
        </div>
      </div>

      {state.loading ? (
        <p className="text-sm text-text-muted">Loading…</p>
      ) : state.error ? (
        <p className="text-sm text-danger" data-testid="paused-tiffins-error">{state.error}</p>
      ) : state.items.length === 0 ? (
        <p className="text-sm text-text-muted" data-testid="paused-tiffins-empty">
          {showAll ? "No paused tiffins found." : "No tiffins are paused today."}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="paused-tiffins-list">
          {state.items.map((item) => (
            <Link
              key={item.subscription_id}
              to={`/subscriptions/${item.subscription_id}`}
              className="rounded-md border border-border bg-background px-3 py-1.5 text-xs transition-colors hover:border-primary"
              data-testid={`paused-tiffin-chip-${item.subscription_id}`}
            >
              <span className="font-medium text-text-main">{item.customer_name || `#${item.customer_id}`}</span>
              <span className="ml-1 text-text-muted">
                (until {formatDate(item.pause_to)}{item.status_label !== "active" ? `, ${item.status_label}` : ""})
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
